import { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import type { MessageDescriptor } from 'react-intl';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowsRotate } from '@fortawesome/pro-solid-svg-icons/faArrowsRotate';
import { faPen } from '@fortawesome/pro-regular-svg-icons/faPen';

import { Button } from 'ui-components/components/button';
import { Gap } from 'ui-components/components/flex';
import { FlexColumn } from 'ui-components/components/flex/column';
import { FlexRow } from 'ui-components/components/flex/row';
import { Input } from 'ui-components/components/input';
import { InlineMessage, INLINE_MESSAGE_TYPES } from 'ui-components/components/inline-message';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { messages } from './barcode-field.messages';
import style from './barcode-field.module.less';

const BARCODE_PATTERN = /^[a-zA-Z0-9\-._]+$/;
const BARCODE_MAX_LENGTH = 100;

export type BarcodeFieldError = 'required' | 'invalidChars' | null;

const ERROR_MESSAGES: Record<NonNullable<BarcodeFieldError>, MessageDescriptor> = {
  required: messages.required,
  invalidChars: messages.invalidChars,
};

type BarcodeFieldProps = {
  value: string;
  isAuto: boolean;
  hasError?: boolean;
  errorType?: BarcodeFieldError;
  isDuplicate?: boolean;
  onChangeValue: (value: string) => void;
  onToggleManual: () => void;
  onRegenerate: () => void;
  onUseAuto: () => void;
  onBlur?: () => void;
};

export const BarcodeField = ({
  value,
  isAuto,
  hasError,
  errorType,
  isDuplicate,
  onChangeValue,
  onToggleManual,
  onRegenerate,
  onUseAuto,
  onBlur,
}: BarcodeFieldProps) => {
  const intl = useIntl();

  const handleChange = useCallback(
    (v: string) => {
      if (v.length <= BARCODE_MAX_LENGTH) {
        onChangeValue(v);
      }
    },
    [onChangeValue],
  );

  if (isAuto) {
    return (
      <>
        <div className={`${style['barcode-chip']} ${hasError ? style['barcode-chip-error'] : ''}`}>
          <span className={style['auto-tag']}>
            <FormattedMessage {...messages.autoTag} />
          </span>
          <span className={style['barcode-value']} title={value}>
            {value}
          </span>
          <Button
            ariaLabel={intl.formatMessage(messages.regenerateAriaLabel)}
            className={style['icon-btn']}
            onClick={onRegenerate}
            theme={THEME_CONSTANTS.unstyled}
          >
            <FontAwesomeIcon icon={faArrowsRotate} />
          </Button>
          <Button
            ariaLabel={intl.formatMessage(messages.editAriaLabel)}
            className={style['icon-btn']}
            onClick={onToggleManual}
            theme={THEME_CONSTANTS.unstyled}
          >
            <FontAwesomeIcon icon={faPen} />
          </Button>
        </div>
        {hasError && (
          <InlineMessage type={INLINE_MESSAGE_TYPES.error}>
            <FormattedMessage {...messages.required} />
          </InlineMessage>
        )}
        {isDuplicate && (
          <InlineMessage type={INLINE_MESSAGE_TYPES.warning}>
            <FormattedMessage {...messages.duplicate} />
          </InlineMessage>
        )}
      </>
    );
  }

  return (
    <FlexRow gap={Gap.Space8}>
      <FlexColumn className={style['manual-input']}>
        <Input hasError={hasError} maxLength={BARCODE_MAX_LENGTH} onBlur={onBlur} onChange={handleChange} value={value} />
        {hasError && errorType && (
          <InlineMessage type={INLINE_MESSAGE_TYPES.error}>
            <FormattedMessage {...ERROR_MESSAGES[errorType]} />
          </InlineMessage>
        )}
        {isDuplicate && (
          <InlineMessage type={INLINE_MESSAGE_TYPES.warning}>
            <FormattedMessage {...messages.duplicate} />
          </InlineMessage>
        )}
        {value.length >= BARCODE_MAX_LENGTH && (
          <InlineMessage type={INLINE_MESSAGE_TYPES.warning}>
            <FormattedMessage {...messages.maxLength} />
          </InlineMessage>
        )}
      </FlexColumn>
      <Button className={style['use-auto-btn']} onClick={onUseAuto} theme={THEME_CONSTANTS.secondary}>
        <FormattedMessage {...messages.useAutoButton} />
      </Button>
    </FlexRow>
  );
};

export const validateBarcodeManual = (value: string): BarcodeFieldError => {
  if (!value.trim()) return 'required';
  if (!BARCODE_PATTERN.test(value)) return 'invalidChars';
  return null;
};
